import { useSelector } from "react-redux";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import toast from "react-hot-toast";
import { updateUserAsync } from "../../../slices/user";
import { useDispatch } from "react-redux";
import PMListbox from "../../../components/utils/PMListbox";
import PMSwitch from "../../../components/utils/PMSwitch";
import {
  CheckBadgeIcon,
  MapPinIcon,
  ClockIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { fetchUser } from "../../../api/users";
import { useEffect, useState } from "react";
import { useSettings } from "../../../contexts/settings-context";
import { setCurrentUser } from "../../../slices/session";
import { Link } from "react-router-dom";
import { MicrosoftConnectPanel } from "../../../components/dashboard/microsoft/MicrosoftConnectPanel";
import {
  disconnectUserMicrosoft,
  fetchUserMicrosoftStatus,
} from "../../../api/microsoft";
import { useTranslation } from "react-i18next";
import { DocumentUploadSectionV5 } from "../../../components/dashboard/Media/DocumentUploadSectionV5";
import { SUPPORTED_LANGUAGES } from "../../../i18n/languages";

export const UserSettings = (props) => {
  const currentUser = useSelector((state) => state.session.currentUser);
  const { settings, saveSettings } = useSettings();

  const [userSettings, setUserSettings] = useState({
    preferred_maps_service: currentUser?.preferred_maps_service,
    language: settings.language,
    time_registration_format: currentUser?.time_registration_format || "hours_minutes",
    use_legacy_variables: currentUser?.use_legacy_variables || false,
    calendar_show_week_numbers:
      currentUser?.calendar_show_week_numbers ??
      currentUser?.settings?.calendar_show_week_numbers ??
      false,
    signature_url: currentUser?.signature_url || "",
  });

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const fetchMicrosoftStatus = async () => {
    try {
      return await fetchUserMicrosoftStatus();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404 || status === 501) {
        return { connected: false, apiMissing: true };
      }
      return { connected: false };
    }
  };

  const getUser = async () => {
    await fetchUser(currentUser.id, {
      fields:
        "id,preferred_maps_service,locale,time_registration_format,use_legacy_variables,calendar_show_week_numbers,signature_url",
    })
      .then((response) => {
        setUserSettings({
          preferred_maps_service: response.data.preferred_maps_service,
          language: response.data.locale,
          time_registration_format: response.data.time_registration_format || "hours_minutes",
          use_legacy_variables: response.data.use_legacy_variables || false,
          calendar_show_week_numbers: response.data.calendar_show_week_numbers || false,
          signature_url: response.data.signature_url || "",
        });
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  };

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    setUserSettings((prev) => ({ ...prev, language: settings.language }));
  }, [settings.language]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#signature") return;
    const el = document.getElementById("signature");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [userSettings.signature_url]);

  const mapsServices = [
    {
      name: "preferred_maps_service",
      value: "GOOGLE_MAPS",
      label: "Google Maps",
      icon: CheckBadgeIcon,
    },
    {
      name: "preferred_maps_service",
      value: "APPLE_MAPS",
      label: "Apple Maps",
      icon: MapPinIcon,
    },
  ];

  // Labels are each language's own name so a user can always find their language.
  const languages = SUPPORTED_LANGUAGES.map((lang) => ({
    name: "language",
    value: lang.apiLocale,
    label: lang.nativeName,
  }));

  const timeRegistrationFormats = [
    { name: "time_registration_format", value: "hours_minutes", label: "Timer og minutter" },
    { name: "time_registration_format", value: "decimal", label: "Kun tid (desimal)" },
  ];

  const persistSignatureUrl = (url) => {
    const data = { id: currentUser.id, signature_url: url || "" };
    setUserSettings((prev) => ({ ...prev, signature_url: url || "" }));
    dispatch(updateUserAsync(data)).then((result) => {
      if (result?.payload && !result.error) {
        dispatch(
          setCurrentUser({
            ...currentUser,
            signature_url: url || "",
          })
        );
        toast.success(t("user.settings.signature.saved"));
      } else {
        toast.error(t("user.settings.signature.save_error"));
      }
    });
  };

  const handleListboxChange = (e) => {
    const fieldName = e.name ?? e.target?.name;
    const value =
      e.value !== undefined ? e.value : e.checked ?? e.target?.checked;
    const data = {
      id: currentUser.id,
      preferred_maps_service:
        fieldName === "preferred_maps_service"
          ? value
          : userSettings.preferred_maps_service,
      locale: fieldName === "language" ? value : userSettings.language,
      time_registration_format:
        fieldName === "time_registration_format"
          ? value
          : userSettings.time_registration_format,
      use_legacy_variables:
        fieldName === "use_legacy_variables"
          ? value
          : userSettings.use_legacy_variables,
      calendar_show_week_numbers:
        fieldName === "calendar_show_week_numbers"
          ? value
          : userSettings.calendar_show_week_numbers,
    };
    setUserSettings((prev) => ({ ...prev, [fieldName]: value }));

    const updatePromise = dispatch(updateUserAsync(data));

    updatePromise
      .then((result) => {
        if (result?.payload && !result.error) {
          dispatch(
            setCurrentUser({
              ...currentUser,
              preferred_maps_service: data.preferred_maps_service,
              locale: data.locale,
              time_registration_format: data.time_registration_format,
              use_legacy_variables: data.use_legacy_variables,
              calendar_show_week_numbers: data.calendar_show_week_numbers,
            })
          );
          toast.success("Innstillinger oppdatert");
        } else {
          toast.error("Kunne ikke oppdatere innstillinger");
        }
      })
      .catch((error) => {
        console.error("Error updating user settings:", error);
        toast.error("Kunne ikke oppdatere innstillinger");
      });

    if (fieldName === "language") {
      saveSettings({ ...settings, language: value });
    }
  };

  function UserForm() {
    return (
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
          <div className="space-y-5 p-5">
            <PMListbox
              name="preferred_maps_service"
              label={"Ønsket karttjeneste"}
              options={mapsServices}
              selected={userSettings?.preferred_maps_service}
              onChange={handleListboxChange}
            />
            <PMListbox
              name="language"
              label={"Språk"}
              options={languages}
              selected={userSettings?.language}
              onChange={handleListboxChange}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
          <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
            <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
              <ClockIcon className="h-4 w-4" />
            </span>
            <h3 className="text-sm font-bold text-slate-900">Timeregistrering</h3>
          </div>
          <div className="p-5">
            <PMListbox
              name="time_registration_format"
              label={"Format for timeregistrering"}
              options={timeRegistrationFormats}
              selected={userSettings?.time_registration_format}
              onChange={handleListboxChange}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
          <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
            <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
              <CalendarDaysIcon className="h-4 w-4" />
            </span>
            <h3 className="text-sm font-bold text-slate-900">
              {t("user.settings.calendar.section_title")}
            </h3>
          </div>
          <div className="p-5">
            <PMSwitch
              name="calendar_show_week_numbers"
              label={t("user.settings.calendar.show_week_numbers")}
              checked={userSettings?.calendar_show_week_numbers}
              onChange={handleListboxChange}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)] lg:col-span-2">
          <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
            <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
              <EnvelopeIcon className="h-4 w-4" />
            </span>
            <h3 className="text-sm font-bold text-slate-900">
              {t("microsoft.user.settings_section_title")}
            </h3>
            <Link
              to="/dashboard/user/microsoft"
              className="ml-auto text-sm font-semibold text-[#1463C7] hover:text-[#0B3A78]"
            >
              {t("microsoft.user.open_full_page")} →
            </Link>
          </div>
          <div className="p-5">
            <p className="mb-4 text-sm text-slate-600">
              {t("microsoft.user.settings_section_intro")}
            </p>
            <MicrosoftConnectPanel
              variant="user"
              titleKey="microsoft.user.card_title"
              intent="user_mail"
              fetchStatus={fetchMicrosoftStatus}
              onDisconnect={disconnectUserMicrosoft}
            />
          </div>
        </div>

        <div
          id="signature"
          className="scroll-mt-24 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)] lg:col-span-2"
        >
          <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
            <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
              <PencilSquareIcon className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-sm font-bold text-slate-900">
                {t("user.settings.signature.section_title")}
              </h3>
              <p className="text-xs text-slate-500">
                {t("user.settings.signature.section_hint")}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
            <div className="flex h-20 min-w-[220px] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4">
              {userSettings.signature_url ? (
                <img
                  src={userSettings.signature_url}
                  alt={t("user.settings.signature.preview_alt")}
                  className="max-h-16 max-w-full object-contain"
                />
              ) : (
                <span className="text-xs font-medium text-slate-400">
                  {t("user.settings.signature.empty")}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <DocumentUploadSectionV5
                multiple={false}
                module="user"
                moduleParam={currentUser?.id}
                required={false}
                buttonStyle="compact"
                allowedFileTypes="image/png, image/jpeg, image/jpg, image/webp"
                parentCallback={(uploadedFiles) => {
                  const first = Array.isArray(uploadedFiles) ? uploadedFiles[0] : null;
                  const f = first?.file;
                  const url =
                    (f && typeof f.url === "string" && f.url) ||
                    (f?.thumb && typeof f.thumb.url === "string" && f.thumb.url) ||
                    "";
                  if (url) persistSignatureUrl(url);
                }}
              />
              {userSettings.signature_url ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-[#DC2626] hover:underline"
                  onClick={() => persistSignatureUrl("")}
                >
                  {t("user.settings.signature.remove")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader
        subtitle={currentUser.name}
        actions={[]}
        title={"Innstillinger"}
      >
        <div className="mx-auto mt-6 max-w-5xl px-4 sm:px-6">
          <UserForm />
        </div>
      </DashboardHeader>
    </>
  );
};
